import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Upload, FileText, Sparkles, ArrowRight, X } from 'lucide-react';

const UploadPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedOrderId, setUploadedOrderId] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'application/pdf') {
        setFile(droppedFile);
      } else {
        toast.error('Bitte nur PDF-Dateien hochladen');
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setUploadedOrderId(response.data.id);
      toast.success('PDF erfolgreich hochgeladen!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleExtract = async () => {
    if (!uploadedOrderId) return;

    setExtracting(true);
    try {
      const response = await api.post(`/extract-va-facts/${uploadedOrderId}`);
      toast.success('VA-Facts erfolgreich extrahiert!');
      
      // Navigate to editor with extracted data
      navigate('/editor', { 
        state: { 
          vaFacts: response.data.va_facts, 
          orderId: uploadedOrderId 
        } 
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Extraktion fehlgeschlagen');
    } finally {
      setExtracting(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setUploadedOrderId(null);
    setUploadProgress(0);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fade-in" data-testid="upload-page">
      <div>
        <h1 className="font-serif text-3xl font-bold text-burgundy">PDF hochladen</h1>
        <p className="text-charcoal-500 mt-2">
          Laden Sie eine Auftragsbestätigung hoch, um automatisch die VA-Facts zu extrahieren
        </p>
      </div>

      {/* Upload Area */}
      {!uploadedOrderId ? (
        <Card className="shadow-card" data-testid="upload-card">
          <CardContent className="p-8">
            <div
              className={`
                border-2 border-dashed rounded-lg p-12 text-center transition-all duration-300
                ${dragActive 
                  ? 'border-burgundy bg-burgundy-50' 
                  : file 
                    ? 'border-gold bg-gold-50' 
                    : 'border-stone-300 hover:border-burgundy'
                }
              `}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="dropzone"
            >
              {file ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="h-12 w-12 text-gold" />
                    <div className="text-left">
                      <p className="font-semibold text-charcoal-800">{file.name}</p>
                      <p className="text-sm text-charcoal-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={resetUpload}
                      className="ml-4"
                      data-testid="remove-file-btn"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {uploading && (
                    <div className="space-y-2">
                      <Progress value={uploadProgress} className="h-2" />
                      <p className="text-sm text-charcoal-500">{uploadProgress}% hochgeladen</p>
                    </div>
                  )}

                  {!uploading && (
                    <Button
                      onClick={handleUpload}
                      className="bg-burgundy hover:bg-burgundy-800 text-white font-serif gap-2"
                      data-testid="upload-btn"
                    >
                      <Upload className="h-4 w-4" />
                      Hochladen
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="h-16 w-16 mx-auto text-stone-400" />
                  <div>
                    <p className="font-semibold text-charcoal-700">
                      PDF hier ablegen
                    </p>
                    <p className="text-sm text-charcoal-500 mt-1">
                      oder klicken um eine Datei auszuwählen
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-input"
                    data-testid="file-input"
                  />
                  <label htmlFor="file-input">
                    <Button
                      variant="outline"
                      className="border-burgundy text-burgundy hover:bg-burgundy-50 cursor-pointer"
                      asChild
                    >
                      <span>Datei auswählen</span>
                    </Button>
                  </label>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Extraction Card */
        <Card className="shadow-card border-l-4 border-l-gold" data-testid="extract-card">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-burgundy flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-gold" />
              PDF erfolgreich hochgeladen
            </CardTitle>
            <CardDescription>
              Jetzt können die VA-Facts automatisch extrahiert werden
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-lg">
              <FileText className="h-8 w-8 text-burgundy" />
              <div>
                <p className="font-semibold text-charcoal-800">{file?.name}</p>
                <p className="text-sm text-charcoal-500">Bereit zur Extraktion</p>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={handleExtract}
                disabled={extracting}
                className="flex-1 bg-burgundy hover:bg-burgundy-800 text-white font-serif gap-2"
                data-testid="extract-btn"
              >
                {extracting ? (
                  <>
                    <Sparkles className="h-4 w-4 animate-spin" />
                    Wird extrahiert...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    VA-Facts extrahieren
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={resetUpload}
                className="border-stone-300"
                data-testid="reset-btn"
              >
                Andere Datei
              </Button>
            </div>

            <p className="text-xs text-charcoal-400 text-center">
              Die KI analysiert das PDF und extrahiert automatisch alle relevanten Informationen
            </p>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <Card className="bg-stone-50 border-stone-200">
        <CardContent className="p-6">
          <h3 className="font-serif font-semibold text-burgundy mb-3">So funktioniert's:</h3>
          <ol className="space-y-2 text-sm text-charcoal-600">
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-6 h-6 bg-burgundy text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
              <span>Laden Sie eine PDF-Auftragsbestätigung hoch</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-6 h-6 bg-burgundy text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
              <span>Die KI extrahiert automatisch alle VA-Facts</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-6 h-6 bg-burgundy text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
              <span>Bearbeiten und speichern Sie das Function Sheet</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-6 h-6 bg-burgundy text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
              <span>Veröffentlichen Sie es für Service und Küche</span>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default UploadPage;
